import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import {getInsertableColumnInfos} from 'notebook/data-tables/DataTableUtil';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {TableFunction} from 'notebook/tables/constants/TableConstants';
import LookupField from 'notebook/tables/excess/functions/lookupField';
import {LookupType} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class DataTableConfigContextHandlerConnector extends DataConnector<
  DataTableConfigContextHandlerProps,
  ConnectedProps
> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps(): ConnectedProps {
    const assaySchema = this.props.dataTableConfig.assaySchema;
    return {
      selectorsById: this.getOrFetchMultipleRequired(
        ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
        getSelectorIdsFromFields(assaySchema.fields)
      ),
    };
  }
}

interface DataTableConfigContextHandlerProps {
  dataTableConfig: DataTableConstants.DataTableConfig;
  entryId: string;
  children: (args: {
    additionalFunctions: Array<TableFunction>;
    insertableColumnInfos: Array<InsertableColumnInfo>;
    getDisallowedColumnNames: () => Array<string>;
  }) => JSX.Element;
  forRun: boolean;
}

class DataTableConfigContextHandler extends React.Component<
  DataTableConfigContextHandlerProps & ConnectedProps
> {
  _handleLookupFieldClicked = ({
    rowData,
    onUpdateSelectedCell,
  }: {
    rowData: RowData | null;
    onUpdateSelectedCell: (cellData: Partial<Cell>) => void;
  }): ReturnType<typeof LookupField.onCreateFormula> => {
    const supportedLookupTypes = [LookupType.SAMPLE];
    const entryType = APIUtil.apiIdToType(this.props.entryId);
    if ([ModelTypeConstants.STAGE_CONFIGURATION, ModelTypeConstants.STAGE_ENTRY].includes(entryType)) {
      supportedLookupTypes.push(LookupType.STAGE_RUN_INPUT, LookupType.EXPERIMENTAL_CONDITION);
    }
    return LookupField.onCreateFormula({
      entryType,
      stageConfigurationId: entryType === ModelTypeConstants.STAGE_CONFIGURATION ? this.props.entryId : null,
      supportedLookupTypes,
      rowData,
      onUpdateSelectedCell,
      dataTableConfig: this.props.dataTableConfig,
    });
  };

  _getAdditionalFunctions(): Array<TableFunction> {
    if (this.props.dataTableConfig.linkType == null) {
      // The table doesn't have a sample column
      return [];
    }
    return [
      {
        name: LookupField.name,
        asyncOnClick: this._handleLookupFieldClicked,
      },
    ];
  }

  _getDisallowedColumnNames = (): Array<string> => this.props.dataTableConfig.columns.map((col) => col.name);

  override render(): JSX.Element {
    return this.props.children({
      additionalFunctions: this._getAdditionalFunctions(),
      insertableColumnInfos: getInsertableColumnInfos(
        this.props.dataTableConfig,
        this.props.selectorsById,
        this.props.forRun
      ),
      getDisallowedColumnNames: this._getDisallowedColumnNames,
    });
  }
}

export default DataTableConfigContextHandlerConnector.connect(DataTableConfigContextHandler);
